// Drawing Studio shared helpers: uid, prim geometry, transforms, AI op matching.
import { tc } from "../../lib/theme";
let _uid = 0;
export const uid = () => `el_${Date.now().toString(36)}_${(_uid++).toString(36)}`;

export const SHEET_W = 1600;
export const SHEET_H = 1130;

export const LAYER_ORDER = ["OUTLINE", "HIDDEN", "CENTER", "HATCH", "DIMENSION", "TEXT", "MARKUP"];

export const LAYER_STYLES = {
  OUTLINE: { color: "#1a1a1a", width: 1.8, dash: null, label: "Outline (0.7mm)" },
  HIDDEN: { color: "#6b7280", width: 0.9, dash: [7, 4], label: "Hidden (0.35mm)" },
  CENTER: { color: "#b91c1c", width: 0.8, dash: [14, 5, 3, 5], label: "Center (0.25mm)" },
  DIMENSION: { color: "#1d4ed8", width: 0.9, dash: null, label: "Dimension (0.25mm)" },
  TEXT: { color: "#374151", width: 0.8, dash: null, label: "Text" },
  HATCH: { color: "#9ca3af", width: 0.6, dash: null, label: "Hatch (0.15mm)" },
  MARKUP: { get color() { return tc("red"); }, width: 1.4, dash: [6, 4], label: "Markup (review)" },
};

export const DASH_OPTIONS = { solid: null, dashed: [7, 4], center: [14, 5, 3, 5], hidden: [4, 3] };

export function withUids(elements) {
  return (elements || []).map((e) => ({ ...e, uid: e.uid || uid() }));
}

export function stripForSave(elements) {
  return elements.map((e) => {
    const { uid, ...rest } = e;
    return rest;
  });
}

export function primCenter(p) {
  switch (p.kind) {
    case "line": return { x: (p.x1 + p.x2) / 2, y: (p.y1 + p.y2) / 2 };
    case "rect": return { x: p.x + p.w / 2, y: p.y + p.h / 2 };
    case "circle": return { x: p.cx, y: p.cy };
    case "text": return { x: p.x, y: p.y };
    case "poly": {
      const xs = p.pts.map((q) => q[0]); const ys = p.pts.map((q) => q[1]);
      return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
    }
    default: return { x: 0, y: 0 };
  }
}

export function translatePrim(p, dx, dy) {
  const q = { ...p };
  if (q.kind === "line") { q.x1 += dx; q.y1 += dy; q.x2 += dx; q.y2 += dy; }
  else if (q.kind === "rect") { q.x += dx; q.y += dy; }
  else if (q.kind === "circle") { q.cx += dx; q.cy += dy; }
  else if (q.kind === "text") { q.x += dx; q.y += dy; }
  else if (q.kind === "poly") { q.pts = q.pts.map(([a, b]) => [a + dx, b + dy]); }
  return q;
}

export function rotatePrim(p, angleDeg, center = null) {
  const c = center || primCenter(p);
  const rad = (angleDeg * Math.PI) / 180;
  const rot = (x, y) => {
    const dx = x - c.x, dy = y - c.y;
    return [c.x + dx * Math.cos(rad) - dy * Math.sin(rad), c.y + dx * Math.sin(rad) + dy * Math.cos(rad)];
  };
  const q = { ...p };
  if (q.kind === "line") {
    [q.x1, q.y1] = rot(q.x1, q.y1); [q.x2, q.y2] = rot(q.x2, q.y2);
  } else if (q.kind === "rect") {
    const corners = [[q.x, q.y], [q.x + q.w, q.y], [q.x + q.w, q.y + q.h], [q.x, q.y + q.h]].map(([a, b]) => rot(a, b));
    return { layer: q.layer, kind: "poly", pts: corners, close: true, uid: q.uid };
  } else if (q.kind === "circle") {
    [q.cx, q.cy] = rot(q.cx, q.cy);
  } else if (q.kind === "text") {
    [q.x, q.y] = rot(q.x, q.y);
    q.rotate = (q.rotate || 0) + angleDeg;
  } else if (q.kind === "poly") {
    q.pts = q.pts.map(([a, b]) => rot(a, b));
  }
  return q;
}

// Bake a Konva node's transform (after Transformer) back into primitive coords.
export function bakeNodeTransform(el, node) {
  const sx = node.scaleX(), sy = node.scaleY();
  const rot = node.rotation();
  const q = { ...el };
  if (el.kind === "line" || el.kind === "poly") {
    const rad = (rot * Math.PI) / 180;
    const tx = node.x(), ty = node.y();
    const xf = ([a, b]) => {
      const x = a * sx, y = b * sy;
      return [tx + x * Math.cos(rad) - y * Math.sin(rad), ty + x * Math.sin(rad) + y * Math.cos(rad)];
    };
    if (el.kind === "line") {
      [q.x1, q.y1] = xf([el.x1, el.y1]); [q.x2, q.y2] = xf([el.x2, el.y2]);
    } else {
      q.pts = el.pts.map(xf);
    }
  } else if (el.kind === "rect") {
    const nw = el.w * sx, nh = el.h * sy;
    if (Math.abs(rot) > 0.5) {
      const x = node.x(), y = node.y();
      const rad = (rot * Math.PI) / 180;
      const xf = (dx, dy) => [x + dx * Math.cos(rad) - dy * Math.sin(rad), y + dx * Math.sin(rad) + dy * Math.cos(rad)];
      return { layer: el.layer, kind: "poly", uid: el.uid, close: true, pts: [xf(0, 0), xf(nw, 0), xf(nw, nh), xf(0, nh)] };
    }
    q.x = node.x(); q.y = node.y(); q.w = nw; q.h = nh;
  } else if (el.kind === "circle") {
    q.cx = node.x(); q.cy = node.y(); q.r = el.r * (sx + sy) / 2;
  } else if (el.kind === "text") {
    q.x = node.x(); q.y = node.y();
    q.size = Math.max(4, el.size * sy);
    q.rotate = (el.rotate || 0) + rot;
  }
  return q;
}

export function resetNode(node) {
  node.scaleX(1); node.scaleY(1); node.rotation(0);
}

// Build composite linear-dimension prims (ISO style) between p1,p2 placed at `place`.
export function buildLinearDim(p1, p2, place) {
  const horizontal = Math.abs(p2.x - p1.x) >= Math.abs(p2.y - p1.y);
  const C = LAYER_STYLES.DIMENSION.color;
  const prims = [];
  const arrow = (x, y, dir) => {
    const s = 8;
    let pts;
    if (dir === "l") pts = [[x, y], [x + s, y - 3], [x + s, y + 3]];
    else if (dir === "r") pts = [[x, y], [x - s, y - 3], [x - s, y + 3]];
    else if (dir === "u") pts = [[x, y], [x - 3, y + s], [x + 3, y + s]];
    else pts = [[x, y], [x - 3, y - s], [x + 3, y - s]];
    prims.push({ kind: "poly", layer: "DIMENSION", pts, close: true, fill: C, dim: true });
  };
  if (horizontal) {
    const y = place.y;
    const v = Math.abs(p2.x - p1.x);
    prims.push({ kind: "line", layer: "DIMENSION", x1: p1.x, y1: y, x2: p2.x, y2: y, dim: true, dim_value: v });
    prims.push({ kind: "line", layer: "DIMENSION", x1: p1.x, y1: p1.y, x2: p1.x, y2: y, dim: true });
    prims.push({ kind: "line", layer: "DIMENSION", x1: p2.x, y1: p2.y, x2: p2.x, y2: y, dim: true });
    arrow(p1.x, y, "l"); arrow(p2.x, y, "r");
    prims.push({ kind: "text", layer: "DIMENSION", x: (p1.x + p2.x) / 2, y: y - 6, s: String(Math.round(v)), size: 11, anchor: "middle", dim: true, dim_value: v });
  } else {
    const x = place.x;
    const v = Math.abs(p2.y - p1.y);
    prims.push({ kind: "line", layer: "DIMENSION", x1: x, y1: p1.y, x2: x, y2: p2.y, dim: true, dim_value: v });
    prims.push({ kind: "line", layer: "DIMENSION", x1: p1.x, y1: p1.y, x2: x, y2: p1.y, dim: true });
    prims.push({ kind: "line", layer: "DIMENSION", x1: p2.x, y1: p2.y, x2: x, y2: p2.y, dim: true });
    arrow(x, p1.y, "u"); arrow(x, p2.y, "d");
    prims.push({ kind: "text", layer: "DIMENSION", x: x - 6, y: (p1.y + p2.y) / 2, s: String(Math.round(v)), size: 11, anchor: "end", rotate: -90, dim: true, dim_value: v });
  }
  return prims;
}

// Match elements for AI ops. Returns indices into elements array.
export function matchElements(elements, match = {}) {
  let idxs = [];
  elements.forEach((e, i) => {
    if (e.auto) return;
    if (match.kind && e.kind !== match.kind) return;
    if (match.layer && e.layer !== match.layer) return;
    if (match.text_contains && !String(e.s || "").toLowerCase().includes(String(match.text_contains).toLowerCase())) return;
    idxs.push(i);
  });
  if (match.near && typeof match.near.x === "number" && idxs.length) {
    let best = idxs[0], bd = Infinity;
    for (const i of idxs) {
      const c = primCenter(elements[i]);
      const d = (c.x - match.near.x) ** 2 + (c.y - match.near.y) ** 2;
      if (d < bd) { bd = d; best = i; }
    }
    idxs = [best];
  }
  return idxs.slice(0, 50);
}

// Apply AI ops to a clone of elements; returns { ghosts, apply } where ghosts are preview prims.
export function previewOps(elements, ops) {
  const ghosts = [];
  // an engineering pass replaces the previous annotation block instead of stacking on it
  if ((ops || []).some((o) => o.eng)) {
    for (const e of elements) if (e.eng) ghosts.push({ ...e, ghost: true, ghostDelete: true });
  }
  for (const op of ops || []) {
    if (op.op === "move") {
      for (const i of matchElements(elements, op.match)) {
        ghosts.push({ ...translatePrim(elements[i], Number(op.dx) || 0, Number(op.dy) || 0), ghost: true });
      }
    } else if (op.op === "delete") {
      for (const i of matchElements(elements, op.match)) {
        ghosts.push({ ...elements[i], ghost: true, ghostDelete: true });
      }
    } else if (op.op === "change_text") {
      for (const i of matchElements(elements, op.match)) {
        ghosts.push({ ...elements[i], s: String(op.new_text ?? elements[i].s), ghost: true });
      }
    } else if (op.op === "add_text") {
      ghosts.push({ kind: "text", layer: op.layer || "TEXT", x: +op.x || 0, y: +op.y || 0, s: String(op.s || ""), size: +op.size || 10, ghost: true });
    } else if (op.op === "add_rect") {
      ghosts.push({ kind: "rect", layer: op.layer || "OUTLINE", x: +op.x || 0, y: +op.y || 0, w: +op.w || 10, h: +op.h || 10, ghost: true });
    } else if (op.op === "add_circle") {
      ghosts.push({ kind: "circle", layer: op.layer || "OUTLINE", cx: +op.cx || 0, cy: +op.cy || 0, r: +op.r || 5, ghost: true });
    }
  }
  return ghosts.map((g) => ({ ...g, uid: uid() }));
}

export function applyOps(elements, ops) {
  let out = (ops || []).some((o) => o.eng) ? elements.filter((e) => !e.eng) : [...elements];
  for (const op of ops || []) {
    if (op.op === "move") {
      const idxs = matchElements(out, op.match);
      out = out.map((e, i) => (idxs.includes(i) ? translatePrim(e, Number(op.dx) || 0, Number(op.dy) || 0) : e));
    } else if (op.op === "delete") {
      const idxs = new Set(matchElements(out, op.match));
      out = out.filter((_, i) => !idxs.has(i));
    } else if (op.op === "change_text") {
      const idxs = matchElements(out, op.match);
      out = out.map((e, i) => (idxs.includes(i) ? { ...e, s: String(op.new_text ?? e.s) } : e));
    } else if (op.op === "add_text") {
      out.push({ kind: "text", layer: op.layer || "TEXT", x: +op.x || 0, y: +op.y || 0, s: String(op.s || ""), size: +op.size || 10, ...(op.eng ? { eng: true } : {}), uid: uid() });
    } else if (op.op === "add_rect") {
      out.push({ kind: "rect", layer: op.layer || "OUTLINE", x: +op.x || 0, y: +op.y || 0, w: +op.w || 10, h: +op.h || 10, ...(op.eng ? { eng: true } : {}), uid: uid() });
    } else if (op.op === "add_circle") {
      out.push({ kind: "circle", layer: op.layer || "OUTLINE", cx: +op.cx || 0, cy: +op.cy || 0, r: +op.r || 5, uid: uid() });
    }
  }
  return out;
}
